/**
 * Portfolio API Client
 * Endpoints for portfolio CRUD operations
 */

import { apiRequest } from './client';

export interface Portfolio {
  id: string;
  name: string;
  description?: string;
  symbols: string[];
  type: 'STATIC' | 'DYNAMIC';
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  metadata?: {
    total_stocks: number;
    [key: string]: unknown;
  };
}

export interface CreatePortfolioRequest {
  name: string;
  description?: string;
  symbols: string[];
  type: 'STATIC' | 'DYNAMIC';
}

export interface UpdatePortfolioRequest {
  name?: string;
  description?: string;
  symbols?: string[];
  type?: 'STATIC' | 'DYNAMIC';
}

/**
 * Get all portfolios
 */
export async function getPortfolios(): Promise<Portfolio[]> {
  return apiRequest<Portfolio[]>('/api/portfolios');
}

/**
 * Get portfolio by ID
 */
export async function getPortfolio(id: string): Promise<Portfolio> {
  return apiRequest<Portfolio>(`/api/portfolios/${id}`);
}

/**
 * Get active portfolio
 */
export async function getActivePortfolio(): Promise<Portfolio> {
  return apiRequest<Portfolio>('/api/portfolios/active');
}

/**
 * Get active portfolio symbols
 */
export async function getActiveSymbols(): Promise<string[]> {
  const response = await apiRequest<{ symbols: string[] }>('/api/portfolios/active/symbols');
  return response.symbols;
}

/**
 * Create new portfolio
 */
export async function createPortfolio(data: CreatePortfolioRequest): Promise<Portfolio> {
  return apiRequest<Portfolio>('/api/portfolios', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update portfolio
 */
export async function updatePortfolio(id: string, data: UpdatePortfolioRequest): Promise<Portfolio> {
  return apiRequest<Portfolio>(`/api/portfolios/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Activate portfolio
 */
export async function activatePortfolio(id: string): Promise<{ active: boolean }> {
  return apiRequest<{ active: boolean }>(`/api/portfolios/${id}/activate`, {
    method: 'POST',
  });
}

/**
 * Delete portfolio
 */
export async function deletePortfolio(id: string): Promise<void> {
  await apiRequest<{ deleted: boolean }>(`/api/portfolios/${id}`, {
    method: 'DELETE',
  });
}
